import type { ExpressCheckoutUpdateOptions } from './types';
export { generateId } from './utils/generate-id';

const DECIMAL_CURRENCY_POLICY = {
  minorUnitsPerMajorUnit: 100,
  fractionDigits: 2,
} as const;

/** Formats legacy Express Checkout amounts, whose contract uses two minor-unit digits. */
export function formatDecimalMinorAmount(amount: number): string {
  return (amount / DECIMAL_CURRENCY_POLICY.minorUnitsPerMajorUnit).toFixed(
    DECIMAL_CURRENCY_POLICY.fractionDigits,
  );
}

/** Converts the decimal amount returned by the legacy Apple Pay bridge to minor units. */
export function parseDecimalMajorAmount(amount: string): number {
  return Math.round(Number.parseFloat(amount) * DECIMAL_CURRENCY_POLICY.minorUnitsPerMajorUnit);
}

// ─── Live key detection ───────────────────────────────────────────────────────

export function isLiveKey(publishableKey: string): boolean {
  return publishableKey.startsWith('pk_live_');
}

// ─── Apple Pay JS SDK loader ──────────────────────────────────────────────────

let applePayScriptPromise: Promise<void> | null = null;
export function loadApplePayScript(): Promise<void> {
  if (applePayScriptPromise) return applePayScriptPromise;

  applePayScriptPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    // <apple-pay-button> custom element already registered — SDK already loaded
    if (customElements?.get('apple-pay-button')) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.async = true;
    script.onload = () => resolve();
    // Non-Apple browsers won't expose ApplePaySession — treat as unavailable, not an error
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return applePayScriptPromise;
}

// ─── Google Pay JS API loader ─────────────────────────────────────────────────

let gpayScriptPromise: Promise<void> | null = null;

export function loadGooglePayScript(): Promise<void> {
  if (gpayScriptPromise) return gpayScriptPromise;

  gpayScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('No window'));
      return;
    }
    if ((globalThis as any).google?.payments?.api?.PaymentsClient) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Pay script'));
    document.head.appendChild(script);
  });

  return gpayScriptPromise;
}

// ─── Google Pay PaymentDataRequestUpdate builder ──────────────────────────────

/**
 * Converts SDK update options into the Google Pay `PaymentDataRequestUpdate` shape.
 * Pure function — no class state needed beyond currency and countryCode.
 */
export function buildGpayPaymentDataUpdate(
  update: Partial<ExpressCheckoutUpdateOptions>,
  currency: string,
  countryCode: string = 'DE',
): object {
  const result: any = {};

  if (update.amount !== undefined) {
    result.newTransactionInfo = {
      totalPriceStatus: 'FINAL',
      totalPriceLabel: 'Total',
      totalPrice: formatDecimalMinorAmount(update.amount),
      currencyCode: currency,
      countryCode,
    };
  }

  if (update.shippingOptions?.length) {
    result.newShippingOptionParameters = {
      defaultSelectedOptionId: update.shippingOptions[0].id,
      shippingOptions: update.shippingOptions.map((so) => ({
        id: so.id,
        label: so.label,
        description: so.detail ?? `${formatDecimalMinorAmount(so.amount)} ${currency}`,
      })),
    };
  }

  if (update.status === 'invalid_shipping_address') {
    result.error = {
      reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
      message: 'Cannot ship to this address.',
      intent: 'SHIPPING_ADDRESS',
    };
  } else if (update.status === 'invalid_shipping_option') {
    result.error = {
      reason: 'SHIPPING_OPTION_INVALID',
      message: 'This shipping option is not available.',
      intent: 'SHIPPING_OPTION',
    };
  }

  return result;
}
